import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, CircularProgress, IconButton, Tooltip, useTheme, useMediaQuery, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDispatch } from 'react-redux';
import Positions from './Positions';
import PageTabs from '../Common/PageTabs';
import { fetchPositionsAction } from '../../store/actions/portfolio';
import { fetchFundsView } from '../../store/actions/funds';

const PORTFOLIO_TABS = [{ label: 'Positions', value: 0 }];

function Portfolio() {
  const [activeTab, setActiveTab] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    dispatch(fetchPositionsAction('historical'));
    dispatch(fetchFundsView());
  }, [dispatch]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    const minVisible = new Promise((resolve) => setTimeout(resolve, 600));
    try {
      await Promise.all([
        dispatch(fetchPositionsAction('historical')),
        dispatch(fetchFundsView()),
        minVisible,
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch]);

  // Add touch handling to prevent parent scroll
  const handleTouchMove = (event) => {
    const element = event.currentTarget;
    const isAtTop = element.scrollTop === 0;
    const isAtBottom = element.scrollHeight - element.scrollTop === element.clientHeight;

    if ((isAtTop && event.touches[0].clientY > 0) ||
      (isAtBottom && event.touches[0].clientY < 0)) {
      event.preventDefault();
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      position: 'relative',
      bgcolor: 'background.default',
      overflow: 'hidden',
      [theme.breakpoints.down('sm')]: {
        // 56 AppBar + 30 Top Ticker = 86 top; + 56 BottomNav = 142
        height: 'calc(100vh - 142px)',
        position: 'fixed',
        top: 86,
        left: 0,
        right: 0,
        bottom: 56
      }
    }}>
      <PageTabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        tabs={PORTFOLIO_TABS}
        sticky
        action={
          isMobile ? (
            <Tooltip title={refreshing ? 'Refreshing…' : 'Refresh'}>
              <IconButton onClick={handleRefresh} size="small" color="primary">
                {refreshing ? (
                  <CircularProgress size={18} thickness={5} color="primary" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              startIcon={
                refreshing ? (
                  <CircularProgress size={16} thickness={5} color="inherit" />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={handleRefresh}
              size="small"
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          )
        }
      />

      <Box
        onTouchMove={handleTouchMove}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          touchAction: isMobile ? 'none' : 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'none',
          height: isMobile ? 'calc(100% - 40px)' : '100%'
        }}
      >
        {activeTab === 0 ? (
          <Box sx={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            height: '100%'
          }}>
            <Positions />
          </Box>
        ) : (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}>
            <Typography
              variant="h6"
              color="text.secondary"
            >
              Holdings Coming Soon
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Portfolio;
